/* eslint-disable react-hooks/exhaustive-deps */

import { useCallback, useEffect, useRef, useState } from 'react'
import { Trans, useTranslation } from 'react-i18next'
import clsx from 'clsx'

import { AlignCenter16, AlignLeft16, AlignRight16 } from '@makyoapp/makyo-icon/icon'
import { Field, SelectDropdown, SingleIconSelect, Slider } from '@makyoapp/makyo-ui'

import { useAppOverlaysContext } from '@/context/AppOverlaysContext'
import { useBreakpoint } from '@/hooks/useBreakpoint'
import { BUILDER_DEVICES } from '@/shared/builder-constants'
import {
  BuilderOverlay,
  formatDrawerSize,
  getDrawerSize,
  getOverlaySizeLabel,
  overlayShapeOptions,
} from '@/shared/overlay'
import { debounce } from '@/shared/utils'
import { Accordion, AccordionItem } from '@/components/Accordion/Accordion'
import { BrandKitColorsDropdown } from '@/components/BrandKitColorsDropdown'

interface OverlaySettingDesignProps {
  overlay: BuilderOverlay
  index: number
  search: string
}

export const OverlaySettingDesign = ({ overlay, index, search }: OverlaySettingDesignProps) => {
  const { t } = useTranslation()
  const { updateOverlay } = useAppOverlaysContext()

  const { device } = useBreakpoint()
  const ActiveDeviceIcon = BUILDER_DEVICES[device].Icon

  // State lokal untuk mengelola data overlay yang sedang diedit
  const [localOverlay, setLocalOverlay] = useState<BuilderOverlay>(overlay)
  const [, setTempTitle] = useState(overlay.title)
  const [tempSizeValue, setTempSizeValue] = useState<number>(getDrawerSize(overlay.size[device]).v1)
  const [tempHeightValue, setTempHeightValue] = useState<number>(() => {
    const size = overlay.size[device]
    const [, height] = size.split(',')

    // If height is undefined or "undefined", return default value
    if (!height || height === 'undefined') {
      return 50
    }

    // Remove % and parse as integer, fallback to 50 if parsing fails
    const heightValue = parseInt(height.replace('%', ''))
    return isNaN(heightValue) ? 50 : heightValue
  })
  const currentSizeRef = useRef<string>(localOverlay.size[device])

  const [hasLocalChanges, setHasLocalChanges] = useState(false)

  // Update localOverlay ketika overlay prop berubah
  useEffect(() => {
    // Hanya update jika tidak ada perubahan lokal
    if (!hasLocalChanges) {
      setLocalOverlay(overlay)
    }
  }, [overlay, hasLocalChanges])

  // Update temp values ketika localOverlay berubah
  useEffect(() => {
    setTempTitle(localOverlay.title)
    setTempSizeValue(getDrawerSize(localOverlay.size[device]).v1)

    const size = localOverlay.size[device]
    const [, height] = size.split(',')
    if (!height || height === 'undefined') {
      setTempHeightValue(50)
    } else {
      const heightValue = parseInt(height.replace('%', ''))
      setTempHeightValue(isNaN(heightValue) ? 50 : heightValue)
    }
  }, [localOverlay, device])

  useEffect(() => {
    setTempTitle(overlay.title)
  }, [overlay.title])

  const [updatedValue, setUpdatedValue] = useState<{ key: string; value: any } | null>(null)
  const [isApplying, setIsApplying] = useState(false)

  const updateLocalAndAPI = useCallback(
    (updater: (overlay: BuilderOverlay) => void) => {
      setLocalOverlay((prev) => {
        const updated = { ...prev }
        updater(updated)
        return updated
      })
      updateOverlay(index, updater)
      setHasLocalChanges(true)
    },
    [updateOverlay, index]
  )

  const localOverlayRef = useRef(localOverlay)
  useEffect(() => {
    localOverlayRef.current = localOverlay
  }, [localOverlay])

  // Create a debounced update function for size changes
  const debouncedSizeUpdate = useCallback(
    debounce((newSize: number) => {
      const currentOverlay = localOverlayRef.current
      const currentSize = currentSizeRef.current

      const value = formatDrawerSize('side', currentSize, `${newSize}%`)
      currentSizeRef.current = value

      const updatedOverlay = { ...currentOverlay }

      const size = {
        ...localOverlay.size,
        [device]: value,
      }

      updateOverlay(index, (prev) => {
        Object.assign(prev, updatedOverlay)
        prev.size = size
      })

      setUpdatedValue({ key: 'size', value })

      setHasLocalChanges(true)
    }, 250),
    [device, index, updateOverlay]
  )

  // Create a debounced update function for height changes
  const debouncedHeightUpdate = useCallback(
    debounce((newHeight: number) => {
      const currentOverlay = localOverlayRef.current
      const currentSize = currentSizeRef.current

      const value = formatDrawerSize('center', currentSize, `${newHeight}%`)
      currentSizeRef.current = value

      const updatedOverlay = { ...currentOverlay }

      const size = {
        ...currentOverlay.size,
        [device]: value,
      }

      updateOverlay(index, (prev) => {
        Object.assign(prev, updatedOverlay)
        prev.size = size
      })

      setUpdatedValue({ key: 'size', value })
      setHasLocalChanges(true)
    }, 250),
    [device, index, updateOverlay]
  )

  const handleSizeChange = useCallback(
    (val: number[]) => {
      setTempSizeValue(val[0])
      debouncedSizeUpdate(val[0])
    },
    [debouncedSizeUpdate]
  )

  const handleHeightChange = useCallback(
    (val: number[]) => {
      setTempHeightValue(val[0])
      debouncedHeightUpdate(val[0])
    },
    [debouncedHeightUpdate]
  )

  // Apply changes to the overlay
  const handleApplyChanges = async () => {
    if (!updatedValue) return

    setIsApplying(true)

    updateLocalAndAPI((prev) => {
      const key = updatedValue.key as keyof BuilderOverlay
      const prevValues = prev[key]

      const updatedPrevValues = Object.keys(prevValues).reduce(
        (acc, device) => {
          acc[device] = updatedValue.value
          return acc
        },
        {} as typeof prevValues
      )

      // @ts-expect-error
      prev[key] = { ...updatedPrevValues }
    })

    setIsApplying(false)

    setUpdatedValue(null)
    setHasLocalChanges(false) // Reset flag
  }

  const isCenter = localOverlay.placement[device] === 'center'

  const ButtonApplyToAll = () => {
    return (
      <>
        <hr className='mt-2 mb-4 bdr' />

        <div className='w-[calc(100% - 24px)] mb-4 flex justify-center'>
          <button
            className={clsx(
              'text-gray-100 text-xs py-2 px-3 rounded-sm w-full transition-colors',
              isApplying
                ? 'bg-primary-700 dark:bg-primary-700/85 cursor-wait'
                : 'bg-primary-500 dark:bg-primary-500/85 hover:bg-primary-600'
            )}
            onClick={handleApplyChanges}
            disabled={isApplying}
          >
            {isApplying ? t('Saving changes...') : t('Save changes across all screen sizes')}
          </button>
        </div>
      </>
    )
  }

  const getFilteredAccordionItems = useCallback(() => {
    if (!search.trim()) {
      return {
        showBgColor: true,
        showSize: true,
        showShape: true,
        showAlignment: true,
      }
    }

    const searchLower = search.toLowerCase()

    return {
      showBgColor:
        'background'.includes(searchLower) ||
        'background color'.includes(searchLower) ||
        'backdrop transparancy'.includes(searchLower) ||
        'opacity'.includes(searchLower),
      showSize: 'size'.includes(searchLower) || 'height'.includes(searchLower),
      showShape: 'shape'.includes(searchLower) || 'corner radius'.includes(searchLower),
      showAlignment: 'alignment'.includes(searchLower) || 'content alignment'.includes(searchLower),
    }
  }, [search])

  const filteredItems = getFilteredAccordionItems()

  return (
    <Accordion openMultiple={false} initialActiveItems={['bgColor']}>
      {filteredItems.showBgColor && (
        <AccordionItem itemId='bgColor' title={<Trans>Background</Trans>}>
          <Field isVertical>
            <label>Background Color</label>
            <BrandKitColorsDropdown
              type='bg'
              value={localOverlay.bgColor}
              onChange={async (opt) => {
                updateLocalAndAPI((prev) => {
                  prev.bgColor = opt
                })
              }}
            />
          </Field>

          <Field isVertical>
            <label>Backdrop Transparancy</label>
            <Slider
              values={[Number(localOverlay.opacity)]}
              max={100}
              min={30}
              step={1}
              onChange={(val) => {
                updateLocalAndAPI((prev) => {
                  prev.opacity = val[0]
                })
              }}
              renderLabel={(val) => `${val}%`}
            />
          </Field>
        </AccordionItem>
      )}

      {filteredItems.showSize && (
        <AccordionItem
          itemId='size'
          title={<Trans>Size</Trans>}
          righSection={<ActiveDeviceIcon className='w-3.5 h-3.5' />}
        >
          <Field isVertical>
            <label>{getOverlaySizeLabel(localOverlay.placement[device]!)}</label>
            <Slider
              values={[tempSizeValue]}
              max={100}
              min={10}
              step={1}
              onChange={handleSizeChange}
              renderLabel={(val) => `${val}%`}
            />
          </Field>

          {isCenter && (
            <Field isVertical>
              <label>Height</label>
              <Slider
                values={[tempHeightValue]}
                max={100}
                min={10}
                step={1}
                onChange={handleHeightChange}
                renderLabel={(val) => `${val}%`}
              />
            </Field>
          )}

          {updatedValue?.key === 'size' && <ButtonApplyToAll />}
        </AccordionItem>
      )}

      {filteredItems.showShape && (
        <AccordionItem
          itemId='shape'
          title={<Trans>Corner Radius</Trans>}
          righSection={<ActiveDeviceIcon className='w-3.5 h-3.5' />}
        >
          <Field>
            <SelectDropdown
              value={overlayShapeOptions.find((opt) => opt.value === localOverlay.shape[device])}
              options={overlayShapeOptions}
              renderOption={(opt) => opt.label}
              onChange={(opt) => {
                updateLocalAndAPI((prev) => {
                  prev.shape = {
                    ...prev.shape,
                    [device]: opt.value,
                  }
                  setUpdatedValue({ key: 'shape', value: opt.value })
                })
              }}
            />
          </Field>

          {updatedValue?.key === 'shape' && <ButtonApplyToAll />}
        </AccordionItem>
      )}

      {filteredItems.showAlignment && (
        <AccordionItem itemId='alignment' title={<Trans>Alignment</Trans>}>
          <Field>
            <label>Content Alignment</label>

            <SingleIconSelect
              id='overlay-alignment'
              onClick={async (opt) => {
                updateLocalAndAPI((prev) => {
                  prev.alignment = opt.id as any
                })
              }}
              options={[
                {
                  icon: <AlignLeft16 />,
                  id: 'start',
                  title: 'Start',
                },
                {
                  icon: <AlignCenter16 />,
                  id: 'center',
                  title: 'Center',
                },
                {
                  icon: <AlignRight16 />,
                  id: 'end',
                  title: 'End',
                },
              ]}
              selected={
                ['start', 'center', 'end'].includes(localOverlay.alignment as string)
                  ? (localOverlay.alignment as string)
                  : 'center'
              }
            />
          </Field>
        </AccordionItem>
      )}
    </Accordion>
  )
}
